import '#core/global.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { type AnyRoute, createRouter, type Router } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import React, { Suspense } from 'react';
import reactDom from 'react-dom/client';

import { App } from '#core/app.tsx';
import { DefaultError } from '#core/components/DefaultError.tsx';
import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { RootErrorBoundary } from '#core/components/layout/RootErrorBoundary.tsx';
import { config as defaultConfig } from '#core/config.default.ts';
import { DuneAppAuthProvider } from '#core/contexts/DuneAppAuthProvider.tsx';
import { MsalAppAuthProvider } from '#core/contexts/MsalAppAuthProvider.tsx';
import { ProjectProvider } from '#core/contexts/ProjectProvider.tsx';
import { env } from '#core/lib/utils/env.ts';
import type { routeTree as coreRouteTree } from '#core/routeTree.gen.ts';
import { createQueryClient, motionConfigTransition, setupRuntime } from '#core/runtime.ts';
import type { ProjectConfigInput } from '#core/schema/ProjectConfigSchema.ts';

export function init<RouteTreeType extends AnyRoute, const ConfigType extends ProjectConfigInput>(
  routeTree: RouteTreeType,
  projectConfig: ConfigType,
  AppContext: React.ComponentType<React.PropsWithChildren> = React.Fragment,
) {
  setupRuntime();

  const router: Router<RouteTreeType, 'never', true> = createRouter({
    routeTree,
    context: {
      queryClient,
      cogniteClient: null,
    },
    defaultErrorComponent: DefaultError,
    defaultStructuralSharing: true,
    scrollRestoration: true,
    basepath: env.basePath,
  });

  const root = document.getElementById('root');
  const AuthProvider = import.meta.env.VITE_APP_AUTH_PROVIDER === 'dune' ? DuneAppAuthProvider : MsalAppAuthProvider;

  if (root) {
    const RootElement = env.strictMode ? React.StrictMode : React.Fragment;
    reactDom.createRoot(root).render(
      <RootElement>
        <RootErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <ProjectProvider config={projectConfig ?? defaultConfig}>
              <Suspense fallback={<LabeledLoader label="Authenticating..." />}>
                <AuthProvider>
                  <MotionConfig transition={motionConfigTransition}>
                    <AppContext>
                      <App router={router} />
                    </AppContext>
                  </MotionConfig>
                </AuthProvider>
              </Suspense>
            </ProjectProvider>
          </QueryClientProvider>
        </RootErrorBoundary>
      </RootElement>,
    );
  }

  return router;
}

export type CoreRouter = Router<typeof coreRouteTree>;

declare module '@tanstack/react-router' {
  interface Register {
    router: CoreRouter;
  }

  interface StaticDataRouteOption {
    route?: {
      icon: React.FunctionComponent;
      sidebarPosition?: number;
      title: string;
    };
  }
}

const queryClient = createQueryClient();
